import { useEffect, useRef, useState } from "react";
import type {
  ChangeEvent,
  ClipboardEvent,
  FocusEvent,
  KeyboardEvent,
} from "react";

export const CODE_LENGTH = 6;

export interface CodeInputProps {
  value: string;
  error: boolean;
  labelledBy: string;
  disabled?: boolean;
  // Bump to return focus to box 0, e.g. after a request that disabled the boxes.
  focusRequest?: number;
  onChange: (value: string) => void;
}

function toChars(value: string): string[] {
  return Array.from({ length: CODE_LENGTH }, (_, i) => value[i] ?? "");
}

function replaceAt(chars: string[], index: number, char: string): string[] {
  const next = [...chars];
  next[index] = char;
  return next;
}

export function CodeInput({
  disabled,
  error,
  focusRequest,
  labelledBy,
  value,
  onChange,
}: CodeInputProps) {
  // Six positional slots (not a shifting queue); join('') under-reports on a gap, which is all Verify needs.
  const [chars, setChars] = useState<string[]>(() => toChars(value));
  const lastValueRef = useRef(value);
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    if (value !== lastValueRef.current) {
      lastValueRef.current = value;
      setChars(toChars(value));
    }
  }, [value]);

  useEffect(() => {
    inputsRef.current[0]?.focus();
  }, [focusRequest]);

  function commit(next: string[], focusIndex: number | null) {
    setChars(next);
    const joined = next.join("");
    lastValueRef.current = joined;
    onChange(joined);
    if (focusIndex !== null) {
      inputsRef.current[focusIndex]?.focus();
    }
  }

  function spreadFrom(index: number, digits: string) {
    const next = [...chars];
    for (let i = 0; i < digits.length && index + i < CODE_LENGTH; i++) {
      next[index + i] = digits[i];
    }
    commit(next, Math.min(index + digits.length, CODE_LENGTH - 1));
  }

  function handleChange(index: number, e: ChangeEvent<HTMLInputElement>) {
    const digits = e.target.value.replace(/\D/g, "");
    if (digits.length === 0) {
      commit(replaceAt(chars, index, ""), index);
      return;
    }
    if (digits.length === 1) {
      commit(
        replaceAt(chars, index, digits),
        Math.min(index + 1, CODE_LENGTH - 1),
      );
      return;
    }
    spreadFrom(index, digits);
  }

  function handleKeyDown(index: number, e: KeyboardEvent<HTMLInputElement>) {
    if (
      /^\d$/.test(e.key) &&
      !e.altKey &&
      !e.ctrlKey &&
      !e.metaKey &&
      chars[index]
    ) {
      // Deterministic replace-and-advance: a refocused-but-not-reselected box would otherwise merge with onChange.
      e.preventDefault();
      commit(
        replaceAt(chars, index, e.key),
        Math.min(index + 1, CODE_LENGTH - 1),
      );
      return;
    }
    if (e.key === "Backspace") {
      e.preventDefault();
      if (chars[index]) {
        commit(replaceAt(chars, index, ""), index);
      } else if (index > 0) {
        commit(replaceAt(chars, index - 1, ""), index - 1);
      }
      return;
    }
    if (e.key === "ArrowLeft" && index > 0) {
      e.preventDefault();
      inputsRef.current[index - 1]?.focus();
      return;
    }
    if (e.key === "ArrowRight" && index < CODE_LENGTH - 1) {
      e.preventDefault();
      inputsRef.current[index + 1]?.focus();
    }
  }

  function handlePaste(index: number, e: ClipboardEvent<HTMLInputElement>) {
    e.preventDefault();
    const digits = e.clipboardData.getData("text").replace(/\D/g, "");
    if (digits) {
      spreadFrom(index, digits);
    }
  }

  function handleFocus(e: FocusEvent<HTMLInputElement>) {
    e.target.select();
  }

  return (
    <div aria-labelledby={labelledBy} className="twofactor__digits" role="group">
      {chars.map((char, index) => (
        <input
          aria-invalid={error || undefined}
          aria-label={`Digit ${index + 1} of ${CODE_LENGTH}`}
          autoComplete="one-time-code"
          className={
            error
              ? "twofactor__digit twofactor__digit--error"
              : "twofactor__digit"
          }
          disabled={disabled}
          inputMode="numeric"
          key={index}
          placeholder="0"
          ref={(el) => {
            inputsRef.current[index] = el;
          }}
          value={char}
          onChange={(e) => handleChange(index, e)}
          onFocus={handleFocus}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={(e) => handlePaste(index, e)}
        />
      ))}
    </div>
  );
}
